import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, withModifiers as _withModifiers, Fragment as _Fragment, renderList as _renderList, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "form-section"
}
const _hoisted_2 = {
  key: 1,
  class: "form-section"
}
const _hoisted_3 = {
  key: 2,
  class: "form-section"
}
const _hoisted_4 = {
  key: 0,
  class: "form-section"
}
const _hoisted_5 = { class: "toggle-basic-info-wrapper" }
const _hoisted_6 = {
  key: 0,
  class: "form-section"
}
const _hoisted_7 = {
  key: 4,
  class: "form-section"
}
const _hoisted_8 = {
  key: 5,
  class: "form-section"
}
const _hoisted_9 = { class: "form-section" }
const _hoisted_10 = { class: "readonly-field mb-3" }
const _hoisted_11 = { class: "walk-in-end-picker" }
const _hoisted_12 = {
  class: "walk-in-end-chips",
  role: "radiogroup",
  "aria-labelledby": "walk-in-end-label"
}
const _hoisted_13 = ["aria-checked", "onClick"]
const _hoisted_14 = { class: "walk-in-end-chip__time" }
const _hoisted_15 = { class: "walk-in-end-chip__duration" }
const _hoisted_16 = {
  key: 0,
  class: "text-muted small mt-2 mb-0"
}
const _hoisted_17 = {
  key: 7,
  class: "form-section"
}
const _hoisted_18 = { class: "readonly-field" }
const _hoisted_19 = {
  key: 0,
  class: "form-section"
}
const _hoisted_20 = {
  key: 0,
  class: "form-section"
}
const _hoisted_21 = {
  key: 1,
  class: "form-section"
}
const _hoisted_22 = {
  key: 8,
  class: "form-section"
}
const _hoisted_23 = { class: "readonly-field" }
const _hoisted_24 = {
  key: 0,
  class: "form-section"
}
const _hoisted_25 = { class: "form-check" }
const _hoisted_26 = ["id", "name", "checked", "disabled", "onChange", "onBlur"]
const _hoisted_27 = ["for"]

import {
	computed,
	watch,
	watchEffect,
	nextTick,
	ref,
	onUnmounted,
	inject,
} from "vue";
import { useBookingForm } from "../composables/useBookingForm";
import { useMasterDataHelpers } from "../composables/useMasterDataHelpers";
import { useDateUtils } from "../composables/useDateUtils";
import { getCreationNearDeadlineWarning } from "../utils/bookingTimeConstraints.js";
import BookingTypeSelector from "./BookingTypeSelector.vue";
import BookingPatternSelector from "./BookingPatternSelector.vue";
import BookingBasicInfo from "./BookingBasicInfo.vue";
import BookingDateTime from "./BookingDateTime.vue";
import BookingForOthers from "./BookingForOthers.vue";
import BookingRoomSelector from "./BookingRoomSelector.vue";
import RecurrenceSelector from "./RecurrenceSelector.vue";
import CancellationDatesManager from "./CancellationDatesManager.vue";
import { bookingFormContextKey } from "./booking-modal/bookingFormContext.js";
import {
	floorToQuarterHour,
	buildWalkInEndTimeOptions,
	WALK_IN_MAX_DURATION_MINUTES,
} from "../utils/walkInSelection.js";


export default {
  __name: 'BookingForm',
  props: {
	show: { type: Boolean, default: false },
	mode: { type: String, default: "create" }, // 'create' | 'edit' | 'view'
	booking: { type: Object, default: null },
	selectedSlot: { type: Object, default: null },
	prefillData: { type: Object, default: null },
	isDragDrop: { type: Boolean, default: false },
	isWalkIn: { type: Boolean, default: false },
},
  emits: [
	"save",
	"save-walkin",
	"update",
	"update-series",
	"update-instance",
],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

// Optional bridge to modal footer actions (provided by BookingModal)
const formContext = inject(bookingFormContextKey, null);

// Use master data helpers for permissions
const masterData = useMasterDataHelpers();

// Use date utilities
const { formatDateForInput, formatTimeForInput } = useDateUtils();

// Student basic info toggle state
const showBasicInfo = ref(false);
const studentHasBasicInfoContent = ref(false);

// Auto-expand basic info section when title or description have content
const autoExpandBasicInfo = (title, description) => {
	const hasContent = !!(title?.trim() || description?.trim());
	studentHasBasicInfoContent.value = hasContent;
	if (hasContent && !showBasicInfo.value) {
		showBasicInfo.value = true;
	}
};

// Booking creation constraints state
const currentCreationConstraint = ref(null);
const startDateTime = ref(null);
const creationDeadlineWarning = ref(null);

// Walk-in: ticking "now" for start/end chips (refreshed in startPeriodicUpdates)
const walkInNow = ref(new Date());

const walkInStartDisplay = computed(() =>
	floorToQuarterHour(walkInNow.value).toLocaleTimeString("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
	}),
);

const walkInEndOptions = computed(() =>
	buildWalkInEndTimeOptions(walkInNow.value),
);

// Use TanStack Form composable
const {
	form,
	modalTitle,
	titleValidator,
	descriptionValidator,
	resetForm,
	populateFormFromProps,
	hasFormChanged,
	updateFormChanged,
	walkInShortenedFromMinutes,
} = useBookingForm(props, emit);

const walkInMaxDurationMinutes = WALK_IN_MAX_DURATION_MINUTES;

// Picking an end time supersedes the "selection was shortened" hint
const selectWalkInDuration = (field, durationMinutes) => {
	walkInShortenedFromMinutes.value = null;
	field.handleChange(durationMinutes);
};

// Note: Cancellation dates manager now uses reactive form values directly from form.Subscribe

// Focus first input when modal opens
const focusFirstInput = () => {
	nextTick(() => {
		const firstInput = document.getElementById("bookingTypeSelect");
		if (firstInput && props.mode !== "view") {
			firstInput.focus();
		}
	});
};

// Simple constraint checking using form.Subscribe
const checkTimeConstraints = (startDate, startTime) => {
	const now = new Date();
	const timestamp = now.getTime();

	// Walk-in bookings are deliberately created within the window that would otherwise be
	// "too soon" - never restrict them. This must be checked here (not just skipped in the
	// template) since startPeriodicUpdates() below also calls this function every second
	// regardless of which section is currently rendered.
	if (props.isWalkIn) {
		currentCreationConstraint.value = { lastChecked: timestamp };
		startDateTime.value = null;
		creationDeadlineWarning.value = null;
		return;
	}

	// Only check for students
	if (masterData.userRole.value !== "student") {
		currentCreationConstraint.value = null;
		startDateTime.value = null;
		creationDeadlineWarning.value = null;
		return;
	}

	// Need both date and time
	if (!startDate || !startTime) {
		currentCreationConstraint.value = null;
		startDateTime.value = null;
		creationDeadlineWarning.value = null;
		return;
	}

	// Always check for creation deadline warning regardless of other constraints
	const deadlineWarning = getCreationNearDeadlineWarning(
		startDate,
		startTime,
		masterData.userRole.value,
		masterData.modificationDeadlineSeconds.value,
		300, // 5 minute warning threshold
		props.mode, // Pass the mode ('create', 'edit', etc.)
	);
	creationDeadlineWarning.value = deadlineWarning;

	// Create the start datetime
	const proposedDateTime = new Date(`${startDate}T${startTime}`);
	startDateTime.value = proposedDateTime;

	// Get current time and thresholds
	const earliestSeconds = masterData.earliestBookingSeconds.value; // default 900 (15 min)
	const latestSeconds = masterData.latestBookingSeconds.value; // default 1209600 (14 days)
	const modificationDeadlineSeconds =
		masterData.modificationDeadlineSeconds.value; // default 3600 (1 hour)

	const earliestAllowed = new Date(now.getTime() + earliestSeconds * 1000);
	const latestAllowed = new Date(now.getTime() + latestSeconds * 1000);

	// For edit modes, check if we can still modify the original booking
	if (
		props.mode === "edit" ||
		props.mode === "edit-series" ||
		props.mode === "edit-instance"
	) {
		// Get the original booking start time
		const originalStartTime = props.booking?.startTime
			? new Date(props.booking.startTime)
			: null;

		if (originalStartTime) {
			// Check if the original booking is still modifiable (not within modification deadline)
			const modificationCutoff = new Date(
				originalStartTime.getTime() - modificationDeadlineSeconds * 1000,
			);

			if (now >= modificationCutoff) {
				// Original booking is no longer modifiable
				currentCreationConstraint.value = {
					restricted: true,
					reason: "modification_deadline_passed",
					message:
						"Diese Buchung kann nicht mehr bearbeitet werden, da die " +
						"Bearbeitungsfrist abgelaufen ist.",
					showCountdown: false,
					lastChecked: timestamp,
				};
				return;
			}
		}

		// For edit mode, we still need to respect the time window constraints for the NEW time
		// Check if new proposed time is too soon
		if (proposedDateTime < earliestAllowed) {
			const minutesRequired = Math.ceil(earliestSeconds / 60);
			currentCreationConstraint.value = {
				restricted: true,
				reason: "too_soon_blocked",
				message:
					`Die neue Zeit ist nicht möglich, da Buchungen mindestens ` +
					`${minutesRequired} Minuten im Voraus erfolgen müssen.`,
				showCountdown: false,
				lastChecked: timestamp,
			};
			return;
		}

		// Check if new proposed time is too far in future
		if (proposedDateTime > latestAllowed) {
			const daysLimit = Math.ceil(latestSeconds / (24 * 60 * 60));
			// Calculate when this booking will become possible
			const whenPossible = new Date(
				proposedDateTime.getTime() - latestSeconds * 1000,
			);
			const msUntilPossible = Math.max(
				0,
				whenPossible.getTime() - now.getTime(),
			);

			// If time has passed and booking is now possible, don't restrict
			if (msUntilPossible <= 0) {
				currentCreationConstraint.value = { lastChecked: timestamp }; // No restrictions but track check time
				return;
			}

			currentCreationConstraint.value = {
				restricted: true,
				reason: "too_far",
				message: "Die neue Zeit ist erst möglich in:",
				showCountdown: true,
				minutesRemaining: Math.ceil(msUntilPossible / (1000 * 60)),
				msRemaining: msUntilPossible,
				lastChecked: timestamp,
			};
			return;
		}

		// For edit mode, show countdown if approaching the modification deadline for the original booking
		if (originalStartTime) {
			const modificationCutoff = new Date(
				originalStartTime.getTime() - modificationDeadlineSeconds * 1000,
			);
			const warningTime = new Date(
				modificationCutoff.getTime() - 15 * 60 * 1000,
			); // 15 minutes before cutoff

			if (now >= warningTime && now < modificationCutoff) {
				const msUntilCutoff = Math.max(
					0,
					modificationCutoff.getTime() - now.getTime(),
				);
				currentCreationConstraint.value = {
					restricted: false, // Allow booking but show warning
					reason: "modification_warning",
					message: "Änderungen nur noch möglich für:",
					showCountdown: true,
					minutesRemaining: Math.ceil(msUntilCutoff / (1000 * 60)),
					msRemaining: msUntilCutoff,
					lastChecked: timestamp,
				};
				return;
			}
		}

		// No restrictions for edit mode
		currentCreationConstraint.value = { lastChecked: timestamp };
		return;
	}

	// For create mode, apply the original logic
	const warningThreshold = new Date(
		now.getTime() + (earliestSeconds + 900) * 1000,
	); // +15 minutes for warning

	// Check if too soon (completely blocked)
	if (proposedDateTime < earliestAllowed) {
		const minutesRequired = Math.ceil(earliestSeconds / 60);
		currentCreationConstraint.value = {
			restricted: true,
			reason: "too_soon_blocked",
			message:
				`Buchung ist nicht möglich, da diese mindestens ` +
				`${minutesRequired} Minuten im Voraus erfolgen muss.`,
			showCountdown: false,
			lastChecked: timestamp,
		};
		return;
	}

	// Check if in warning window (show countdown)
	if (proposedDateTime < warningThreshold) {
		// Calculate time until the booking becomes "too soon" (earliestAllowed time)
		const cutoffTime = new Date(
			proposedDateTime.getTime() - earliestSeconds * 1000,
		);
		const msUntilCutoff = Math.max(0, cutoffTime.getTime() - now.getTime());

		currentCreationConstraint.value = {
			restricted: false, // Allow booking but show warning
			reason: "warning_window",
			message: "Buchung ist noch möglich für:",
			showCountdown: true,
			minutesRemaining: Math.floor(msUntilCutoff / (1000 * 60)),
			msRemaining: msUntilCutoff,
			lastChecked: timestamp,
		};
		return;
	}

	// Check if too far in future
	if (proposedDateTime > latestAllowed) {
		const daysLimit = Math.ceil(latestSeconds / (24 * 60 * 60));
		// Calculate when this booking will become possible
		const whenPossible = new Date(
			proposedDateTime.getTime() - latestSeconds * 1000,
		);
		const msUntilPossible = Math.max(0, whenPossible.getTime() - now.getTime());

		// If time has passed and booking is now possible, don't restrict
		if (msUntilPossible <= 0) {
			currentCreationConstraint.value = { lastChecked: timestamp }; // No restrictions but track check time
			return;
		}

		currentCreationConstraint.value = {
			restricted: true,
			reason: "too_far",
			message: "Buchung ist erst möglich in:",
			showCountdown: true,
			minutesRemaining: Math.ceil(msUntilPossible / (1000 * 60)),
			msRemaining: msUntilPossible,
			lastChecked: timestamp,
		};
		return;
	}

	// No restrictions
	currentCreationConstraint.value = { lastChecked: timestamp };
};

// Form validity check - includes time constraints for students
const isFormValid = computed(() => {
	const baseValid = form.state.canSubmit;

	// Trigger constraint check whenever form validity changes to ensure constraints are current
	if (baseValid && masterData.userRole.value === "student") {
		// Check if we need to update constraints when form becomes valid
		try {
			const startDate = form.getFieldValue?.("startDate");
			const startTime = form.getFieldValue?.("startTime");
			if (startDate && startTime) {
				// Only update if we don't have current constraint data or if significant time has passed
				const now = new Date();
				const lastCheck = currentCreationConstraint.value?.lastChecked || 0;
				if (now.getTime() - lastCheck > 2000) {
					// Only recheck every 2 seconds to avoid loops
					checkTimeConstraints(startDate, startTime);
				}
			}
		} catch (error) {
			// Ignore form access errors
		}
	}

	// For students, block only when actually restricted (not for warning window)
	if (
		masterData.userRole.value === "student" &&
		currentCreationConstraint.value?.restricted
	) {
		return false;
	}

	return baseValid;
});

// Check if creation is restricted by time constraints (for button disabling)
const isCreationTimeRestricted = computed(() => {
	return currentCreationConstraint.value?.restricted || false;
});

// Form submission handler
const handleSubmit = () => {
	form.handleSubmit();
};

// Re-run constraint check (e.g. when a creation countdown expires in the footer)
const refreshCreationConstraints = () => {
	try {
		const startDate = form.getFieldValue?.("startDate");
		const startTime = form.getFieldValue?.("startTime");
		if (startDate && startTime) {
			// Re-assigning the same value still triggers form subscribers / constraint logic
			form.setFieldValue("startTime", startTime);
		}
	} catch (error) {
		console.warn("Error triggering constraint update:", error);
	}
};

// Keep modal footer in sync when this form is hosted inside BookingModal
if (formContext) {
	formContext.submit = handleSubmit;
	formContext.focusFirstInput = focusFirstInput;
	formContext.refreshCreationConstraints = refreshCreationConstraints;

	watchEffect(() => {
		formContext.active = props.show;
		formContext.mode = props.mode;
		formContext.isWalkIn = props.isWalkIn;
		formContext.modalTitle = modalTitle.value || "Buchung";
		formContext.isFormValid = isFormValid.value;
		formContext.hasFormChanged = hasFormChanged.value;
		formContext.isCreationTimeRestricted = isCreationTimeRestricted.value;
		formContext.currentCreationConstraint = currentCreationConstraint.value;
		formContext.creationDeadlineWarning = creationDeadlineWarning.value;
		formContext.startDateTime = startDateTime.value;
	});
}

// Check if the booking is for another person (to determine when to show email notification checkbox)
const isBookingForOtherPerson = (formValues) => {
	if (!masterData.masterData.value?.userInfo) return false;

	const currentUserStudentId = masterData.masterData.value.userInfo.student?.id;
	const currentUserEmployeeId =
		masterData.masterData.value.userInfo.employee?.id;

	// Check if booking is for someone else based on form values (create mode)
	if (formValues.createForOther && formValues.selectedPersonId) {
		return true; // Explicitly creating for someone else
	}

	// Check if current booking (in edit mode) is for someone else
	if (props.booking) {
		const bookingStudentId = props.booking.student?.id;
		const bookingEmployeeId = props.booking.employee?.id;

		// Booking is for another person if it has a student/employee ID that doesn't match current user
		const isForDifferentStudent =
			bookingStudentId && bookingStudentId !== currentUserStudentId;
		const isForDifferentEmployee =
			bookingEmployeeId && bookingEmployeeId !== currentUserEmployeeId;

		return isForDifferentStudent || isForDifferentEmployee;
	}

	return false;
};

// Get room display name for view mode
const getRoomDisplayName = (roomId) => {
	if (!roomId) return "";
	const room = masterData.getRoomById.value(roomId);
	if (!room) return "";

	const building = room.building;
	const buildingShort = building?.short || "";
	const doorLabel = room.doorLabel || room.name || "";
	const roomName = room.name && room.doorLabel ? ` ${room.name}` : "";

	return buildingShort
		? `${buildingShort}: ${doorLabel}${roomName}`
		: `${doorLabel}${roomName}`;
};

// Periodic constraint updates for real-time countdown
let constraintUpdateInterval = null;

const startPeriodicUpdates = () => {
	if (constraintUpdateInterval) {
		clearInterval(constraintUpdateInterval);
	}

	if (masterData.userRole.value === "student") {
		constraintUpdateInterval = setInterval(() => {
			try {
				const startDate = form.getFieldValue?.("startDate");
				const startTime = form.getFieldValue?.("startTime");
				if (startDate && startTime) {
					checkTimeConstraints(startDate, startTime);
				}
			} catch (error) {
				// Ignore form access errors
			}
			if (props.isWalkIn) {
				walkInNow.value = new Date();
			}
		}, 1000);
	}
};

// Watch for modal show to populate form and start periodic updates.
// `immediate` is required because BookingModal only mounts this form while the modal is
// open (so the very first render already knows whether it is a walk-in) - without it the
// mount-time `show === true` would never be seen here.
watch(
	() => props.show,
	(isShowing) => {
		if (isShowing) {
			// Only reset form for create mode to avoid clearing pre-populated edit data
			if (props.mode === "create") {
				resetForm();
			}
			// Always reset toggle; autoExpandBasicInfo will re-expand if content exists
			showBasicInfo.value = false;
			populateFormFromProps(props.isDragDrop);
			startPeriodicUpdates();
		} else if (constraintUpdateInterval) {
			clearInterval(constraintUpdateInterval);
			constraintUpdateInterval = null;
		}
	},
	{ immediate: true },
);

// Watch for prefillData changes
watch(
	() => props.prefillData,
	(prefillData) => {
		if (prefillData && props.show && populateFormFromProps) {
			populateFormFromProps(props.isDragDrop);
		}
	},
	{ immediate: true },
);

// Also restart updates when user role changes
watch(
	() => masterData.userRole.value,
	() => {
		if (props.show) {
			startPeriodicUpdates();
		}
	},
);

// Clean up on unmount
onUnmounted(() => {
	if (constraintUpdateInterval) {
		clearInterval(constraintUpdateInterval);
	}
	if (formContext) {
		formContext.active = false;
	}
});

// Keep a minimal expose for imperative focus from the modal shell
__expose({
	focusFirstInput,
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "booking-form",
    onSubmit: _cache[1] || (_cache[1] = _withModifiers((...args) => (_unref(form).handleSubmit && _unref(form).handleSubmit(...args)), ["prevent","stop"]))
  }, [
    _createCommentVNode(" Room Selection "),
    (__props.mode !== 'view' && !__props.isWalkIn)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[2] || (_cache[2] = _createElementVNode("h6", { class: "section-title" }, " Raum ", -1 /* CACHED */)),
          _createVNode(BookingRoomSelector, {
            form: _unref(form),
            disabled: __props.mode === 'view',
            "current-room-id": __props.booking?.roomId || null
          }, null, 8 /* PROPS */, ["form", "disabled", "current-room-id"])
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Booking Type Selection "),
    (
        (__props.mode === 'create' ||
          __props.mode === 'conflict' ||
          __props.mode === 'edit' ||
          __props.mode === 'edit-series' ||
          __props.mode === 'edit-instance') &&
          _unref(masterData).userRole.value !== 'student'
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[3] || (_cache[3] = _createElementVNode("h6", { class: "section-title" }, " Nutzungsart ", -1 /* CACHED */)),
          _createVNode(BookingTypeSelector, {
            form: _unref(form),
            mode: __props.mode
          }, null, 8 /* PROPS */, ["form", "mode"])
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Basic Info (non-students: always show) "),
    (_unref(masterData).userRole.value !== 'student')
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _cache[4] || (_cache[4] = _createElementVNode("h6", { class: "section-title" }, " Allgemeine Informationen ", -1 /* CACHED */)),
          _createVNode(BookingBasicInfo, {
            form: _unref(form),
            disabled: __props.mode === 'view',
            mode: __props.mode,
            booking: __props.booking,
            "title-validator": _unref(titleValidator),
            "description-validator": _unref(descriptionValidator)
          }, null, 8 /* PROPS */, ["form", "disabled", "mode", "booking", "title-validator", "description-validator"])
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Basic Info (students: collapsible in create/edit, conditional in view) "),
    (_unref(masterData).userRole.value === 'student')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
          _createCommentVNode(" Auto-expand when existing content is present "),
          _createVNode(_unref(form).Subscribe, null, {
            default: _withCtx(({ values }) => [
              _createTextVNode(_toDisplayString(autoExpandBasicInfo(values.title, values.description) || ""), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createCommentVNode(" View mode: only show if content exists "),
          (__props.mode === 'view' && studentHasBasicInfoContent.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _cache[5] || (_cache[5] = _createElementVNode("h6", { class: "section-title" }, " Allgemeine Informationen ", -1 /* CACHED */)),
                _createVNode(BookingBasicInfo, {
                  form: _unref(form),
                  disabled: true,
                  mode: __props.mode,
                  booking: __props.booking,
                  "title-validator": _unref(titleValidator),
                  "description-validator": _unref(descriptionValidator)
                }, null, 8 /* PROPS */, ["form", "mode", "booking", "title-validator", "description-validator"])
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Create/Edit modes: collapsible toggle, collapsed by default (also for walk-in) "),
          (__props.mode !== 'view')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("a", {
                    href: "#",
                    class: "toggle-basic-info",
                    onClick: _cache[0] || (_cache[0] = _withModifiers($event => (showBasicInfo.value = !showBasicInfo.value), ["prevent"]))
                  }, [
                    _createElementVNode("i", {
                      class: _normalizeClass(
                showBasicInfo.value ? 'fa fa-chevron-down' : 'fa fa-chevron-right'
              )
                    }, null, 2 /* CLASS */),
                    _cache[6] || (_cache[6] = _createTextVNode(" Titel & Beschreibung hinzufügen (optional) ", -1 /* CACHED */))
                  ])
                ]),
                (showBasicInfo.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(BookingBasicInfo, {
                        form: _unref(form),
                        disabled: false,
                        mode: __props.mode,
                        booking: __props.booking,
                        "title-validator": _unref(titleValidator),
                        "description-validator": _unref(descriptionValidator)
                      }, null, 8 /* PROPS */, ["form", "mode", "booking", "title-validator", "description-validator"])
                    ]))
                  : _createCommentVNode("v-if", true)
              ], 64 /* STABLE_FRAGMENT */))
            : _createCommentVNode("v-if", true)
        ], 64 /* STABLE_FRAGMENT */))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Create for Others "),
    (
        (__props.mode === 'create' || __props.mode === 'conflict') &&
          _unref(masterData).canBookForOthers.value
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _cache[7] || (_cache[7] = _createElementVNode("h6", { class: "section-title" }, " Für andere Person erstellen ", -1 /* CACHED */)),
          _createVNode(BookingForOthers, {
            form: _unref(form),
            disabled: __props.mode === 'view',
            "modal-visible": __props.show
          }, null, 8 /* PROPS */, ["form", "disabled", "modal-visible"])
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Date & Time "),
    (!__props.isWalkIn)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _cache[8] || (_cache[8] = _createElementVNode("h6", { class: "section-title" }, " Datum und Uhrzeit ", -1 /* CACHED */)),
          _createVNode(BookingDateTime, {
            form: _unref(form),
            "can-ignore-opening-hours": 
          __props.mode !== 'view' && _unref(masterData).canIgnoreOpeningHours.value
        ,
            disabled: __props.mode === 'view',
            show: __props.show
          }, null, 8 /* PROPS */, ["form", "can-ignore-opening-hours", "disabled", "show"]),
          _createCommentVNode(" Watch form values and check constraints "),
          _createVNode(_unref(form).Subscribe, null, {
            default: _withCtx(({ values }) => [
              _createTextVNode(_toDisplayString(checkTimeConstraints(values.startDate, values.startTime) || ""), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          })
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
          _createCommentVNode(" Walk-in (Ad-Hoc) booking: start is always floor(now); pick end via chips "),
          _createElementVNode("div", _hoisted_9, [
            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "d-flex align-items-start mb-3" }, [
              _createElementVNode("i", {
                class: "fas fa-bolt me-2 mt-1",
                "aria-hidden": "true"
              }),
              _createElementVNode("div", null, [
                _createElementVNode("strong", null, "Sofortbuchung"),
                _createTextVNode(" — Diese Buchung beginnt sofort (auf 15-Minuten gerundet) und verbraucht keine Punkte. ")
              ])
            ], -1 /* CACHED */)),
            _createElementVNode("div", _hoisted_10, [
              _createTextVNode(" Beginn: " + _toDisplayString(walkInStartDisplay.value) + " Uhr ", 1 /* TEXT */),
              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-muted small ms-1" }, "(jetzt, gerundet)", -1 /* CACHED */))
            ]),
            _createVNode(_unref(form).Field, { name: "durationMinutes" }, {
              default: _withCtx(({ field }) => [
                _createElementVNode("div", _hoisted_11, [
                  _cache[10] || (_cache[10] = _createElementVNode("span", {
                    id: "walk-in-end-label",
                    class: "form-label d-block mb-2"
                  }, " Bis wann? ", -1 /* CACHED */)),
                  _createElementVNode("div", _hoisted_12, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(walkInEndOptions.value, (option) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: option.durationMinutes,
                        type: "button",
                        class: _normalizeClass(["walk-in-end-chip", {
                  'walk-in-end-chip--selected':
                    field.state.value === option.durationMinutes,
                }]),
                        role: "radio",
                        "aria-checked": field.state.value === option.durationMinutes,
                        onClick: $event => (selectWalkInDuration(field, option.durationMinutes))
                      }, [
                        _createElementVNode("span", _hoisted_14, _toDisplayString(option.endLabel), 1 /* TEXT */),
                        _createElementVNode("span", _hoisted_15, _toDisplayString(option.durationLabel), 1 /* TEXT */)
                      ], 10 /* CLASS, PROPS */, _hoisted_13))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  (_unref(walkInShortenedFromMinutes))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_16, " Ihre Auswahl von " + _toDisplayString(_unref(walkInShortenedFromMinutes)) + " Min wurde auf die maximal mögliche Dauer von " + _toDisplayString(_unref(walkInMaxDurationMinutes)) + " Min gekürzt. ", 1 /* TEXT */))
                    : _createCommentVNode("v-if", true)
                ])
              ]),
              _: 1 /* STABLE */
            })
          ])
        ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */)),
    _createCommentVNode(" Room Display (View Mode + Walk-in) "),
    (
        (__props.mode === 'view' && __props.booking?.roomId) ||
          (__props.isWalkIn && __props.selectedSlot?.roomId)
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _cache[12] || (_cache[12] = _createElementVNode("h6", { class: "section-title" }, " Raum ", -1 /* CACHED */)),
          _createElementVNode("div", _hoisted_18, _toDisplayString(getRoomDisplayName(__props.booking?.roomId || __props.selectedSlot?.roomId)), 1 /* TEXT */)
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Booking Pattern Selection "),
    _createVNode(_unref(form).Subscribe, null, {
      default: _withCtx(({ values }) => [
        (
            __props.mode === 'create' &&
              !__props.isWalkIn &&
              _unref(masterData).canCreateSeries.value &&
              !(values.createForOther && values.personType === 'student')
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
              _cache[13] || (_cache[13] = _createElementVNode("h6", { class: "section-title" }, " Buchungsmuster ", -1 /* CACHED */)),
              _createVNode(BookingPatternSelector, {
                form: _unref(form),
                mode: __props.mode
              }, null, 8 /* PROPS */, ["form", "mode"])
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" Series Pattern - with reactive subscription "),
    _createVNode(_unref(form).Subscribe, null, {
      default: _withCtx(({ values }) => [
        (
            values.rrulePattern &&
              __props.mode !== 'view' &&
              __props.mode !== 'edit-instance' &&
              _unref(masterData).canCreateSeries.value &&
              !(values.createForOther && values.personType === 'student')
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _cache[14] || (_cache[14] = _createElementVNode("h6", { class: "section-title" }, " Wiederholungsmuster ", -1 /* CACHED */)),
              _createVNode(RecurrenceSelector, {
                form: _unref(form),
                disabled: __props.mode === 'view'
              }, null, 8 /* PROPS */, ["form", "disabled"])
            ]))
          : _createCommentVNode("v-if", true),
        _createCommentVNode(" Cancellation Dates "),
        (
            values.rrulePattern &&
              __props.mode !== 'view' &&
              __props.mode !== 'edit-instance' &&
              _unref(masterData).canCreateSeries.value &&
              !(values.createForOther && values.personType === 'student')
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
              _cache[15] || (_cache[15] = _createElementVNode("h6", { class: "section-title" }, " Ausfalltermine ", -1 /* CACHED */)),
              (_openBlock(), _createBlock(CancellationDatesManager, {
                key: `${values.rrulePattern || ''}-${values.startDate || ''}-${
              values.startTime || ''
            }`,
                form: _unref(form),
                "rrule-pattern": values.rrulePattern || '',
                "start-date": 
              __props.mode === 'edit-series' && __props.booking?.series?.startTime
                ? _unref(formatDateForInput)(new Date(__props.booking.series.startTime))
                : values.startDate || ''
            ,
                "start-time": 
              __props.mode === 'edit-series' && __props.booking?.series?.startTime
                ? _unref(formatTimeForInput)(new Date(__props.booking.series.startTime))
                : values.startTime || ''
            ,
                disabled: __props.mode === 'view'
              }, null, 8 /* PROPS */, ["form", "rrule-pattern", "start-date", "start-time", "disabled"]))
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" Display selected person in view/edit modes "),
    (
        (__props.mode === 'view' ||
          __props.mode === 'edit' ||
          __props.mode === 'edit-series' ||
          __props.mode === 'edit-instance') &&
          (__props.booking?.student || __props.booking?.employee)
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
          _cache[16] || (_cache[16] = _createElementVNode("h6", { class: "section-title" }, " Erstellt für ", -1 /* CACHED */)),
          _createElementVNode("div", _hoisted_23, [
            (__props.booking.student)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(__props.booking.student.displayName), 1 /* TEXT */)
                ], 64 /* STABLE_FRAGMENT */))
              : (__props.booking.employee)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(__props.booking.employee.displayName), 1 /* TEXT */)
                  ], 64 /* STABLE_FRAGMENT */))
                : _createCommentVNode("v-if", true)
          ])
        ]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Email Notification Settings (only for edit modes when booking is for others) "),
    _createVNode(_unref(form).Subscribe, null, {
      default: _withCtx(({ values }) => [
        (
            (__props.mode === 'edit' ||
              __props.mode === 'edit-series' ||
              __props.mode === 'edit-instance') &&
              _unref(masterData).canManageOtherBookings?.value &&
              isBookingForOtherPerson(values)
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
              _cache[18] || (_cache[18] = _createElementVNode("h6", { class: "section-title" }, " Benachrichtigungseinstellungen ", -1 /* CACHED */)),
              _createVNode(_unref(form).Field, { name: "sendMailNotification" }, {
                default: _withCtx(({ field }) => [
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("input", {
                      id: `sendMailNotification-${__props.mode}`,
                      name: field.name,
                      checked: field.state.value,
                      class: "form-check-input",
                      type: "checkbox",
                      disabled: __props.mode === 'view',
                      onChange: $event => (field.handleChange($event.target.checked)),
                      onBlur: field.handleBlur
                    }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_26),
                    _createElementVNode("label", {
                      for: `sendMailNotification-${__props.mode}`,
                      class: "form-check-label"
                    }, " Per E-Mail benachrichtigen ", 8 /* PROPS */, _hoisted_27)
                  ]),
                  _cache[17] || (_cache[17] = _createElementVNode("small", { class: "form-text text-muted" }, " Bei Aktivierung wird eine E-Mail-Benachrichtigung über die Änderung versendet. ", -1 /* CACHED */))
                ]),
                _: 1 /* STABLE */
              })
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" Form change detection using TanStack Form's Subscribe for reactivity "),
    _createVNode(_unref(form).Subscribe, null, {
      default: _withCtx(({ fieldMeta }) => [
        _createTextVNode(_toDisplayString(_unref(updateFormChanged)(fieldMeta) || ""), 1 /* TEXT */)
      ]),
      _: 1 /* STABLE */
    })
  ], 32 /* NEED_HYDRATION */))
}
}

}